import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, UploadCloud, FileSpreadsheet, Check, AlertTriangle, Eye, Database, Download, RefreshCw, Save, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const IMPORTABLE = ['venues', 'sports', 'schedules', 'teams', 'players', 'games'];

const actionColor = {
  added: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  create: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  changed: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  update: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  deleted: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  removed: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  invalid: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
};

export const OlImporter = ({ open, onOpenChange, orgId, defaultEntity, onCommitted }) => {
  const [manifest, setManifest] = useState([]);
  const [entity, setEntity] = useState(IMPORTABLE.includes(defaultEntity) ? defaultEntity : 'games');
  const [sourceType, setSourceType] = useState('gsheet');
  const [csvText, setCsvText] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [preview, setPreview] = useState(null);        // CSV column-map preview
  const [propagate, setPropagate] = useState(true);
  // migration_log_0169: "Show these games on the website?" — ON-mode only,
  // default ON. Threads to layout-commit as publish_on_website.
  const [publishOnWebsite, setPublishOnWebsite] = useState(true);
  const [publishFlagOn, setPublishFlagOn] = useState(false);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/ol-canonical/publish-flag-mode/${orgId}`, { withCredentials: true })
      .then(r => setPublishFlagOn((r.data?.games_publish_flag_mode) === 'on'))
      .catch(() => {});
  }, [orgId]);
  const [busy, setBusy] = useState(false);
  // Smart Google-Sheet layout import
  const [tabs, setTabs] = useState([]);
  const [gid, setGid] = useState('');
  const [tabName, setTabName] = useState('');
  const [mode, setMode] = useState('amend');
  const [layoutPrev, setLayoutPrev] = useState(null);  // layout diff preview
  const [defaults, setDefaults] = useState({});

  useEffect(() => {
    if (!open) return;
    setEntity(IMPORTABLE.includes(defaultEntity) ? defaultEntity : 'games');
    setPreview(null); setLayoutPrev(null);
    axios.get(`${API}/api/ol-import/entities`, { withCredentials: true })
      .then(r => setManifest(r.data.entities || [])).catch(() => setManifest([]));
    axios.get(`${API}/api/ol-import/${orgId}/defaults`, { withCredentials: true })
      .then(r => setDefaults(r.data.defaults || {})).catch(() => setDefaults({}));
  }, [open, defaultEntity, orgId]);

  const spec = manifest.find(m => m.key === entity);

  // ---- CSV column-map path (unchanged) ----
  const csvBody = useCallback(() => ({
    entity, source_type: 'csv', csv_text: csvText, propagate,
  }), [entity, csvText, propagate]);

  const runCsvPreview = async () => {
    setBusy(true); setPreview(null);
    try {
      const r = await axios.post(`${API}/api/ol-import/${orgId}/preview`, csvBody(), { withCredentials: true });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };
  const runCsvCommit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/ol-import/${orgId}/commit`, csvBody(), { withCredentials: true });
      const d = r.data;
      toast.success(`Imported into ${d.collection}: ${d.created} created, ${d.updated} updated${d.skipped ? `, ${d.skipped} skipped` : ''}`);
      setPreview(null); setCsvText(''); onCommitted?.(entity); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setCsvText(String(reader.result || '')); setPreview(null); };
    reader.readAsText(f);
  };

  // ---- Smart Google-Sheet layout path ----
  const loadTabs = async () => {
    if (!sheetUrl.trim()) return;
    setBusy(true); setTabs([]); setLayoutPrev(null);
    try {
      const r = await axios.post(`${API}/api/ol-import/tabs`, { sheet_url: sheetUrl }, { withCredentials: true });
      const list = r.data.tabs || [];
      setTabs(list);
      if (list.length && !list.find(t => t.gid === gid)) {
        // Preference order (role-agnostic — benefits both mount points):
        //  1. the org's saved default tab for this kind, if present in the list;
        //  2. else, when importing games, a tab whose name matches "Schedule";
        //  3. else the first tab (as before).
        const savedGid = defaults[entity]?.gid;
        const chosen =
          (savedGid && list.find(t => t.gid === savedGid)) ||
          (entity === 'games' && list.find(t => /schedule/i.test(t.name || ''))) ||
          list[0];
        setGid(chosen.gid); setTabName(chosen.name);
      }
      toast.success(`${list.length} tabs found`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not list tabs'); }
    finally { setBusy(false); }
  };

  const applyDefault = (kind) => {
    const d = defaults[kind];
    if (!d) { toast.info(`No saved ${kind} default yet`); return; }
    setSheetUrl(d.sheet_url || ''); setGid(d.gid || ''); setTabName(d.tab_name || '');
    setMode(d.mode || 'amend'); setTabs([]); setLayoutPrev(null);
    toast.success(`Loaded saved ${kind} sheet + tab`);
  };

  const saveDefault = async () => {
    const kind = layoutPrev?.kind; if (!kind) return;
    try {
      await axios.put(`${API}/api/ol-import/${orgId}/defaults`,
        { kind, sheet_url: sheetUrl, gid, tab_name: tabName, mode }, { withCredentials: true });
      setDefaults(d => ({ ...d, [kind]: { sheet_url: sheetUrl, gid, tab_name: tabName, mode } }));
      toast.success(`Saved as the ${kind} default`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save default'); }
  };

  const runLayoutPreview = async () => {
    setBusy(true); setLayoutPrev(null);
    try {
      const r = await axios.post(`${API}/api/ol-import/${orgId}/layout-preview`,
        { sheet_url: sheetUrl, gid, mode }, { withCredentials: true });
      setLayoutPrev(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };
  const runLayoutCommit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/ol-import/${orgId}/layout-commit`,
        { sheet_url: sheetUrl, gid, mode, publish_on_website: publishOnWebsite }, { withCredentials: true });
      const d = r.data;
      const parts = d.kind === 'players'
        ? [`${d.created} added`, `${d.updated} changed`, `${d.removed} removed`]
        : [`${d.created} added`, `${d.updated} changed`, `${d.deleted} deleted`];
      toast.success(`${d.kind}: ${parts.join(', ')} · ${d.enqueued} queued to WP (${d.season})`);
      setLayoutPrev(null); onCommitted?.(d.kind === 'players' ? 'players' : 'games'); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const isSheet = sourceType === 'gsheet';
  const sm = layoutPrev?.summary || {};

  const exportCsv = () => {
    if (!layoutPrev) return;
    const header = ['Action', 'Item', 'Field', 'Old', 'New'];
    const lines = [header];
    (layoutPrev.rows || []).forEach(r => {
      if (r.changes && r.changes.length) {
        r.changes.forEach(c => lines.push([r.action, r.item || r.detail, c.field, c.old, c.new]));
      } else {
        lines.push([r.action, r.item || r.detail, '', '', '']);
      }
    });
    const csv = lines.map(cols => cols.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ol-import-${layoutPrev.kind}-${layoutPrev.mode}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success('Preview exported to CSV');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="ol-importer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-amber-400" /> Import Data into OmniLeagues
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Load a Google Sheet (auto-detects Schedule / Roster layouts) or a MSTW-style CSV straight
            into the OL source of truth. Always <strong className="text-slate-300">Preview</strong> first.
          </DialogDescription>
        </DialogHeader>

        {/* Source toggle */}
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400">Source</label>
          <div className="mt-1 flex gap-2">
            <button onClick={() => { setSourceType('gsheet'); setPreview(null); }} data-testid="ol-importer-source-gsheet"
              className={`flex-1 h-9 rounded-md text-xs font-medium border flex items-center justify-center gap-1.5 ${isSheet ? 'bg-amber-600 text-white border-amber-500' : 'bg-slate-800 text-slate-300 border-slate-600'}`}>
              <FileSpreadsheet className="h-3.5 w-3.5" /> Google Sheet (smart)</button>
            <button onClick={() => { setSourceType('csv'); setLayoutPrev(null); }} data-testid="ol-importer-source-csv"
              className={`flex-1 h-9 rounded-md text-xs font-medium border flex items-center justify-center gap-1.5 ${!isSheet ? 'bg-amber-600 text-white border-amber-500' : 'bg-slate-800 text-slate-300 border-slate-600'}`}>
              <UploadCloud className="h-3.5 w-3.5" /> CSV / columns</button>
          </div>
        </div>

        {isSheet ? (
          <>
            {/* Saved defaults */}
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-slate-400">Load a saved default:</span>
              <button onClick={() => applyDefault('games')} data-testid="ol-importer-default-games"
                className="px-2 py-0.5 rounded border border-slate-600 text-slate-300 hover:bg-slate-800">Games{defaults.games ? '' : ' (none)'}</button>
              <button onClick={() => applyDefault('players')} data-testid="ol-importer-default-players"
                className="px-2 py-0.5 rounded border border-slate-600 text-slate-300 hover:bg-slate-800">Players{defaults.players ? '' : ' (none)'}</button>
            </div>

            {/* URL + load tabs */}
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-400">Google Sheet URL (share “Anyone with the link – Viewer”)</label>
              <div className="mt-1 flex gap-2">
                <input value={sheetUrl} onChange={e => { setSheetUrl(e.target.value); setTabs([]); setLayoutPrev(null); }}
                  placeholder="https://docs.google.com/spreadsheets/d/…/edit"
                  className="flex-1 h-9 rounded-md px-2 bg-slate-800 border border-slate-600 text-sm text-slate-100"
                  data-testid="ol-importer-sheet-url" />
                <Button onClick={loadTabs} disabled={busy || !sheetUrl.trim()} data-testid="ol-importer-load-tabs"
                  className="bg-slate-700 hover:bg-slate-600 text-white h-9">
                  {busy && !tabs.length ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Load tabs</Button>
              </div>
            </div>

            {/* Tab picker + mode */}
            {tabs.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-slate-400">Tab</label>
                  <select value={gid} onChange={e => { setGid(e.target.value); setTabName(tabs.find(t => t.gid === e.target.value)?.name || ''); setLayoutPrev(null); }}
                    className="mt-1 w-full h-9 rounded-md px-2 bg-slate-800 border border-slate-600 text-sm text-slate-100"
                    data-testid="ol-importer-tab">
                    {tabs.map(t => <option key={t.gid} value={t.gid}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-slate-400">Mode</label>
                  <div className="mt-1 flex gap-2">
                    <button onClick={() => { setMode('amend'); setLayoutPrev(null); }} data-testid="ol-importer-mode-amend"
                      className={`flex-1 h-9 rounded-md text-xs border ${mode === 'amend' ? 'bg-emerald-600 text-white border-emerald-500' : 'bg-slate-800 text-slate-300 border-slate-600'}`}
                      title="Precise diff: only add / change / delete what differs from the sheet">Amend (diff)</button>
                    <button onClick={() => { setMode('overwrite'); setLayoutPrev(null); }} data-testid="ol-importer-mode-overwrite"
                      className={`flex-1 h-9 rounded-md text-xs border ${mode === 'overwrite' ? 'bg-amber-600 text-white border-amber-500' : 'bg-slate-800 text-slate-300 border-slate-600'}`}
                      title="Replace the whole season for the sheet's schedules">Overwrite</button>
                  </div>
                </div>
              </div>
            )}

            {/* Layout preview diff */}
            {layoutPrev && (
              <div className="space-y-2" data-testid="ol-importer-layout-preview">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge className="bg-slate-700 text-slate-200">{layoutPrev.layout} · {layoutPrev.kind}</Badge>
                  <Badge className="bg-slate-700 text-slate-200">{layoutPrev.season}</Badge>
                  <Badge className={actionColor.added}>{sm.added || 0} added</Badge>
                  <Badge className={actionColor.changed}>{sm.changed || 0} changed</Badge>
                  <Badge className={actionColor.deleted}>{(sm.deleted ?? sm.removed) || 0} {layoutPrev.kind === 'players' ? 'removed' : 'deleted'}</Badge>
                  <span className="text-slate-500">{layoutPrev.total_in_sheet} in sheet · {layoutPrev.existing_in_scope} in DB</span>
                  <button onClick={exportCsv} data-testid="ol-importer-export-csv"
                    className="ml-auto px-2 py-0.5 rounded border border-slate-600 text-slate-300 hover:bg-slate-800 inline-flex items-center gap-1">
                    <FileDown className="h-3 w-3" /> Export CSV</button>
                </div>
                {(layoutPrev.warnings_total > 0) && (
                  <div className="text-[11px] text-amber-300 bg-amber-950/40 border border-amber-800/50 rounded p-2" data-testid="ol-importer-warnings">
                    <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3 w-3" /> {layoutPrev.warnings_total} unmapped label(s)/token(s) — NOT imported:</div>
                    <div className="mt-1 font-mono">{(layoutPrev.warnings || []).slice(0, 20).map((w, i) => <div key={i}>“{w.label}” — {w.reason}{w.detail ? ` (${w.detail})` : ''}</div>)}</div>
                  </div>
                )}
                {(layoutPrev.unmatched_venues?.length > 0) && (
                  <div className="text-[11px] text-orange-300 bg-orange-950/30 border border-orange-800/40 rounded p-2" data-testid="ol-importer-unmatched-venues">
                    <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3 w-3" /> {layoutPrev.unmatched_venues.length} location(s) not matched to a Venue (games still import; add the venue to link it):</div>
                    <div className="mt-1 font-mono">{layoutPrev.unmatched_venues.slice(0, 12).map((v, i) => <div key={i}>{v}</div>)}</div>
                  </div>
                )}
                {(layoutPrev.skipped_total > 0) && (
                  <div className="text-[11px] text-slate-400">{layoutPrev.skipped_total} non-game / template / prior-season row(s) skipped.</div>
                )}
                <div className="max-h-56 overflow-y-auto rounded-md border border-slate-700">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-800 sticky top-0"><tr className="text-left text-slate-400"><th className="px-2 py-1 w-20">Action</th><th className="px-2 py-1">Detail</th></tr></thead>
                    <tbody>
                      {(layoutPrev.rows || []).map((r, i) => (
                        <tr key={i} className="border-t border-slate-800" data-testid={`ol-importer-lrow-${i}`}>
                          <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded border text-[10px] ${actionColor[r.action]}`}>{r.action}</span></td>
                          <td className="px-2 py-1 text-slate-300 font-mono break-all">{r.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* CSV column-map path */}
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-400">What are you importing?</label>
              <select value={entity} onChange={e => { setEntity(e.target.value); setPreview(null); }}
                className="mt-1 w-full h-9 rounded-md px-2 bg-slate-800 border border-slate-600 text-sm text-slate-100" data-testid="ol-importer-entity">
                {IMPORTABLE.map(k => { const m = manifest.find(x => x.key === k); return <option key={k} value={k}>{m ? m.label : k}</option>; })}
              </select>
            </div>
            {spec && (
              <div className="text-[11px] text-slate-400 bg-slate-800/60 border border-slate-700 rounded-md p-2" data-testid="ol-importer-columns">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-300 font-medium">Recognized columns</span>
                  <a href={`${API}/api/ol-import/template/${entity}`} className="text-amber-300 hover:underline inline-flex items-center gap-1 shrink-0" data-testid="ol-importer-template-link">
                    <Download className="h-3 w-3" /> Download template CSV</a>
                </div>
                <div className="mt-1 font-mono">{spec.columns.join(', ')}</div>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] uppercase tracking-wide text-slate-400">Paste CSV or upload a file</label>
                <label className="text-xs text-amber-300 cursor-pointer hover:underline" data-testid="ol-importer-file-label">
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="ol-importer-file" /> Choose file…</label>
              </div>
              <textarea value={csvText} onChange={e => { setCsvText(e.target.value); setPreview(null); }} rows={6}
                placeholder="header1,header2,header3&#10;value1,value2,value3"
                className="w-full rounded-md p-2 bg-slate-800 border border-slate-600 text-xs font-mono text-slate-100" data-testid="ol-importer-csv-text" />
            </div>
            {preview && (
              <div className="space-y-2" data-testid="ol-importer-preview">
                <div className="flex items-center gap-2 text-xs">
                  <Badge className="bg-slate-700 text-slate-200">{preview.total} rows</Badge>
                  <Badge className={actionColor.create}>{preview.creates} create</Badge>
                  <Badge className={actionColor.update}>{preview.updates} update</Badge>
                  {preview.invalid > 0 && <Badge className={actionColor.invalid}>{preview.invalid} invalid</Badge>}
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row sm:items-center">
          {isSheet && layoutPrev && (
            <button onClick={saveDefault} data-testid="ol-importer-save-default"
              className="text-[11px] text-slate-300 mr-auto inline-flex items-center gap-1 hover:underline">
              <Save className="h-3 w-3" /> Remember as {layoutPrev.kind} default</button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-slate-600 text-slate-300" data-testid="ol-importer-cancel">Cancel</Button>
          {publishFlagOn && isSheet && (!layoutPrev || layoutPrev.kind === 'games') && (
            <label className="text-[11px] text-slate-200 inline-flex items-center gap-2 mr-auto cursor-pointer"
              data-testid="ol-importer-publish-label"
              title="When on, imported games are shown on the public website (same as the Scheduler W).">
              <input type="checkbox" checked={publishOnWebsite}
                onChange={e => setPublishOnWebsite(e.target.checked)}
                className="accent-amber-500 h-4 w-4" data-testid="ol-importer-publish-checkbox" />
              Show these games on the website?
            </label>
          )}
          {isSheet ? (
            <>
              <Button onClick={runLayoutPreview} disabled={!gid || busy} className="bg-slate-700 hover:bg-slate-600 text-white" data-testid="ol-importer-preview-btn">
                {busy && !layoutPrev ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview</Button>
              <Button onClick={runLayoutCommit} disabled={!layoutPrev || busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ol-importer-commit-btn">
                {busy && layoutPrev ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Import</Button>
            </>
          ) : (
            <>
              <Button onClick={runCsvPreview} disabled={!csvText.trim() || busy} className="bg-slate-700 hover:bg-slate-600 text-white" data-testid="ol-importer-preview-btn">
                {busy && !preview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview</Button>
              <Button onClick={runCsvCommit} disabled={!preview || busy || (preview.creates + preview.updates) === 0} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ol-importer-commit-btn">
                {busy && preview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Import {preview ? `(${preview.creates + preview.updates})` : ''}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OlImporter;
